// RenewalPage — /renewal route: send a lease to a unit's EXISTING tenants.
//
// Flow (modeled on TestLeasePage):
//   1. Pick a property → unit.
//   2. "Load current residents" pulls the unit's current renewal signers from
//      AppFolio — Financially-Responsible occupants aged ≥18 plus all
//      Co-signers (under-18 occupants and Other Occupants are excluded).
//   3. The roster is EDITABLE: staff add / remove / edit names + emails before
//      generating. (Names/DOBs come from real AppFolio tenant records.)
//   4. "Create renewal draft → open builder" creates a NORMAL editable lease
//      draft (term + rent pre-defaulted by the cycle logic, animals empty) and
//      navigates to the standard lease builder, where the rest of the flow —
//      configure → Generate → Send → DocuSeal sign — is reused unchanged.
//
// This is a production flow (real tenants get real signing requests), so it is
// NOT marked as a testing tool.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchSelect } from '@/components/SearchSelect';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import {
  createRenewalLease,
  listCurrentResidents,
  listProperties,
  listUnits,
  type PropertyOption,
  type RenewalResidentOption,
  type UnitOption,
  unitOptionLabel,
} from '@/hooks/useLeaseLifecycle';

interface ResidentRow extends RenewalResidentOption {
  key: number;
}

let nextKey = 1;
const blankRow = (): ResidentRow => ({
  key: nextKey++,
  tenantId: '',
  firstName: '',
  lastName: '',
  email: '',
  tenantType: null,
  dateOfBirth: null,
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Short label for the AppFolio TenantType, for the roster badge. */
function tenantTypeLabel(type: string | null): string | null {
  if (!type) return null;
  const t = type.trim().toLowerCase();
  if (t === 'co-signer' || t === 'cosigner') return 'Cosigner';
  if (t === 'financially responsible') return 'Resident';
  return type;
}

export function RenewalPage(): React.JSX.Element {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState<string | null>(null);

  const [propertyId, setPropertyId] = useState('');

  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitId, setUnitId] = useState('');

  // The editable roster + its load lifecycle. `loaded` flips true once a load
  // completes (even with zero rows) so we can distinguish "not loaded yet" from
  // "loaded, but AppFolio has no current adult occupants".
  const [residents, setResidents] = useState<ResidentRow[]>([]);
  const [residentsLoading, setResidentsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProperties();
        if (!cancelled) setProperties(list);
      } catch (err) {
        if (!cancelled) setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
      } finally {
        if (!cancelled) setPropsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load units whenever the property changes. Reset the roster — it's unit-scoped.
  useEffect(() => {
    setResidents([]);
    setLoaded(false);
    if (!propertyId) {
      setUnits([]);
      setUnitId('');
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    listUnits(propertyId)
      .then((list) => {
        if (cancelled) return;
        setUnits(list);
        setUnitId((prev) => (list.some((u) => u.unitId === prev) ? prev : (list[0]?.unitId ?? '')));
      })
      .catch((err) => {
        if (!cancelled) setUnitsError(err instanceof Error ? err.message : 'Failed to load units');
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => { cancelled = true; };
  }, [propertyId]);

  // Changing the unit invalidates a previously-loaded roster.
  useEffect(() => {
    setResidents([]);
    setLoaded(false);
  }, [unitId]);

  const updateResident = (key: number, patch: Partial<RenewalResidentOption>) => {
    setResidents((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const handleLoadResidents = async () => {
    if (!propertyId || !unitId) return;
    setResidentsLoading(true);
    try {
      const { residents: list } = await listCurrentResidents(propertyId, unitId);
      setResidents(list.map((r) => ({ ...r, key: nextKey++ })));
      setLoaded(true);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not load residents (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not load current residents';
      toast.error(msg, { title: 'Load residents' });
    } finally {
      setResidentsLoading(false);
    }
  };

  // A signer needs a name + a valid email to receive the signing request.
  const validResidents = residents.filter(
    (r) => r.firstName.trim() && EMAIL_RE.test((r.email ?? '').trim()),
  );
  const hasInvalidEmail = residents.some(
    (r) => (r.email ?? '').trim() && !EMAIL_RE.test((r.email ?? '').trim()),
  );
  const canCreate = !busy && !!propertyId && !!unitId && validResidents.length > 0 && !hasInvalidEmail;

  const handleCreate = async () => {
    if (!canCreate) return;
    setBusy(true);
    try {
      const { groupId } = await createRenewalLease({
        propertyId,
        unitId,
        residents: validResidents.map((r) => ({
          tenantId: r.tenantId,
          firstName: r.firstName.trim(),
          lastName: r.lastName.trim(),
          email: (r.email ?? '').trim() || null,
          tenantType: r.tenantType,
          dateOfBirth: r.dateOfBirth,
        })),
      });
      navigate(`/applications/${encodeURIComponent(groupId)}/lease`);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: string } | null)?.error ?? `Could not start renewal (HTTP ${err.status})`)
          : err instanceof Error
            ? err.message
            : 'Could not start renewal';
      toast.error(msg, { title: 'Renewal' });
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Renewal — existing tenants
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Send a renewal lease to a unit's current tenants. Pick the property and unit, load the
          current residents (occupants 18+ and any cosigners), edit the roster if needed, then open
          the standard lease builder. Term and rent are pre-filled and stay editable — review them
          before generating.
        </p>
      </div>

      {propsError && (
        <div className="alert" data-tone="danger" role="alert" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{propsError}</div>
        </div>
      )}

      <div className="stack">
        {/* Property + unit picker */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Property &amp; unit</h3>
          </div>
          <div className="stack-sm">
            <div className="field">
              <label htmlFor="prop-select">Property</label>
              <SearchSelect
                id="prop-select"
                options={properties.map((p) => ({
                  value: p.propertyId,
                  label: p.name,
                  sublabel: p.address,
                }))}
                value={propertyId}
                onChange={setPropertyId}
                disabled={propsLoading}
                placeholder={propsLoading ? 'Loading…' : 'Select or search by name or address…'}
                emptyText="No matching properties"
              />
            </div>
            <div className="field">
              <label htmlFor="unit-select">Unit</label>
              <select
                id="unit-select"
                className="input"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                disabled={!propertyId || unitsLoading}
              >
                <option value="">
                  {!propertyId
                    ? 'Select a property first…'
                    : unitsLoading
                      ? 'Loading units…'
                      : units.length === 0
                        ? 'No units found for this property'
                        : 'Select a unit…'}
                </option>
                {units.map((u) => (
                  <option key={u.unitId} value={u.unitId}>
                    {unitOptionLabel(u)}
                    {u.address ? ` — ${u.address}` : ''}
                  </option>
                ))}
              </select>
              {unitsError && <span className="helper" data-state="error">{unitsError}</span>}
            </div>
            <button
              type="button"
              className="btn"
              data-variant="secondary"
              data-size="sm"
              onClick={() => void handleLoadResidents()}
              disabled={!propertyId || !unitId || residentsLoading}
              style={{ alignSelf: 'flex-start' }}
            >
              {residentsLoading ? 'Loading residents…' : 'Load current residents'}
            </button>
          </div>
        </section>

        {/* Roster — editable, pre-filled from AppFolio */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Renewal signers</h3>
          </div>
          {!loaded ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick a property + unit and choose “Load current residents” to pull the current tenants.
            </p>
          ) : (
            <>
              {residents.length === 0 && (
                <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                  <div className="alert-body">
                    AppFolio shows no current adult occupants or cosigners for this unit. You can add
                    signers manually below.
                  </div>
                </div>
              )}
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: '0 0 var(--space-3)' }}>
                Pre-filled from AppFolio (editable). Each signer needs a valid email to receive the
                signing request. The first row is the lead applicant. Animals start empty — add them in
                the builder.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {residents.map((r, idx) => {
                  const email = (r.email ?? '').trim();
                  const emailInvalid = !!email && !EMAIL_RE.test(email);
                  const badge = tenantTypeLabel(r.tenantType);
                  return (
                    <div key={r.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="text"
                          className="input"
                          placeholder={`Signer ${idx + 1} first name`}
                          value={r.firstName}
                          onChange={(e) => updateResident(r.key, { firstName: e.target.value })}
                          aria-label={`Signer ${idx + 1} first name`}
                        />
                        {badge && (
                          <span className="pill" data-tone="neutral" style={{ alignSelf: 'flex-start' }}>
                            {badge}
                          </span>
                        )}
                      </div>
                      <input
                        type="text"
                        className="input"
                        style={{ flex: 1 }}
                        placeholder={`Signer ${idx + 1} last name`}
                        value={r.lastName}
                        onChange={(e) => updateResident(r.key, { lastName: e.target.value })}
                        aria-label={`Signer ${idx + 1} last name`}
                      />
                      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="email"
                          className="input"
                          placeholder="tenant@example.com"
                          value={r.email ?? ''}
                          onChange={(e) => updateResident(r.key, { email: e.target.value })}
                          aria-label={`Signer ${idx + 1} email`}
                          aria-invalid={emailInvalid}
                        />
                        {emailInvalid && (
                          <span className="helper" data-state="error">Enter a valid email.</span>
                        )}
                        {!email && (
                          <span className="helper" data-state="error">Email required to send.</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        data-icon-only
                        aria-label={`Remove signer ${idx + 1}`}
                        onClick={() => setResidents((prev) => prev.filter((x) => x.key !== r.key))}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => setResidents((prev) => [...prev, blankRow()])}
                  style={{ alignSelf: 'flex-start' }}
                >
                  + Add signer
                </button>
              </div>
            </>
          )}
        </section>

        {/* Action */}
        <section className="card">
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Opens the full lease builder, prefilled from this unit's AppFolio data plus the renewal
              roster. Configure, Generate, then Send from the builder — signing goes to the emails above.
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => void handleCreate()}
              disabled={!canCreate}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy ? 'Starting…' : 'Create renewal draft → open builder →'}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
